/**
 * Client-side session · one storage for both form factors.
 *
 * Architecture decision (2026-07-19): single web codebase serves both
 * desktop web and mobile browsers (responsive / PWA-style). No native
 * app in Phase 1. The login screen offers a "workspace" choice —
 * Office (desktop-oriented pages) vs Field (mobile capture pages) —
 * which only affects the post-login landing route, not the auth.
 */

export interface Session {
  token: string;
  expiresAt: string;
  user: {
    id: string;
    tenantId: string;
    email: string;
    name: string;
    role: string;
  };
  tenant: {
    id: string;
    code: string;
    name: string;
    clientType: string;
    tier: string;
    status: string;
  };
  /** "office" | "field" · chosen on the login screen. */
  workspace: "office" | "field";
}

const KEY = "sh.session";
const DEV_KEY = "sh.devBypass";

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (s.expiresAt && new Date(s.expiresAt).getTime() < Date.now()) {
      localStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function saveSession(s: Session) {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export function clearSession() {
  localStorage.removeItem(KEY);
  localStorage.removeItem(DEV_KEY);
}

/** Dev bypass · lets local dev keep working without a login. */
export function setDevBypass() {
  localStorage.setItem(DEV_KEY, "1");
}
export function hasDevBypass(): boolean {
  return localStorage.getItem(DEV_KEY) === "1";
}

export function isAuthenticated(): boolean {
  return getSession() !== null || hasDevBypass();
}

export function getToken(): string | null {
  return getSession()?.token ?? null;
}
